import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

// Bounce to /pricing whenever the backend says the caller needs an active
// subscription.  We avoid the bounce for the /auth/me probe so unpaid users
// can still see who they are and finish checkout.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 402) {
      const path = window.location.pathname;
      if (!path.startsWith('/pricing') && !path.startsWith('/login')) {
        window.location.assign('/pricing?resume=1');
      }
    }
    return Promise.reject(err);
  }
);

export const AuthAPI = {
  register: (body) => api.post('/auth/register', body).then(r => r.data),
  login: (body) => api.post('/auth/login', body).then(r => r.data),
  session: (session_id) => api.post('/auth/session', { session_id }).then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
  logout: () => api.post('/auth/logout').then(r => r.data),
};

export const PlayersAPI = {
  list: () => api.get('/players').then(r => r.data),
  get: (id) => api.get(`/players/${id}`).then(r => r.data),
  create: (body) => api.post('/players', body).then(r => r.data),
  bulk: (players) => api.post('/players/bulk', { players }).then(r => r.data),
  remove: (id) => api.delete(`/players/${id}`).then(r => r.data),
  shareProgress: (id) => api.post(`/players/${id}/share-progress`).then(r => r.data),
  revokeProgress: (id) => api.delete(`/players/${id}/share-progress`).then(r => r.data),
};

export const EvaluationsAPI = {
  list: (playerId) => api.get('/evaluations', { params: playerId ? { player_id: playerId } : {} }).then(r => r.data),
  create: (body) => api.post('/evaluations', body).then(r => r.data),
  remove: (id) => api.delete(`/evaluations/${id}`).then(r => r.data),
};

export const VideosAPI = {
  list: (playerId) => api.get('/videos', { params: playerId ? { player_id: playerId } : {} }).then(r => r.data),
  create: (body) => api.post('/videos', body).then(r => r.data),
  remove: (id) => api.delete(`/videos/${id}`).then(r => r.data),
  share: (id) => api.post(`/videos/${id}/share`).then(r => r.data),
};

export const PublicAPI = {
  clip: (token) => api.get(`/public/clips/${token}`).then(r => r.data),
  progress: (token) => api.get(`/public/progress/${token}`).then(r => r.data),
};

export const TagsAPI = {
  listByVideo: (vid) => api.get(`/videos/${vid}/tags`).then(r => r.data),
  add: (vid, body) => api.post(`/videos/${vid}/tags`, body).then(r => r.data),
  listByPlayer: (pid) => api.get(`/players/${pid}/tags`).then(r => r.data),
  remove: (id) => api.delete(`/tags/${id}`).then(r => r.data),
};

export const StatsAPI = {
  team: () => api.get('/stats/team').then(r => r.data),
};

export const CheckoutAPI = {
  create: (tier) => api.post('/checkout/create', { tier, origin: window.location.origin }).then(r => r.data),
  verify: (session_id) => api.get('/checkout/verify', { params: { session_id } }).then(r => r.data),
  portal: () => api.post('/checkout/portal', { origin: window.location.origin }).then(r => r.data),
};

export const InvitesAPI = {
  list: (playerId) => api.get('/invites', { params: playerId ? { player_id: playerId } : {} }).then(r => r.data),
  create: (playerId) => api.post('/invites', { playerId }).then(r => r.data),
  data: (token) => api.get(`/invites/${token}/data`).then(r => r.data),
};

export const FeedbackAPI = {
  submit: (body) => api.post('/feedback', body).then(r => r.data),
  list: () => api.get('/feedback').then(r => r.data),
};

export default api;
